import axios from 'axios';
import { getApiUrl } from '../utils/envValidator';

const API_URL = getApiUrl();
const CDN_URL = 'https://cdn.themadzone.com';
const R2_URL = 'https://21bcf6a2def0a1f1b96480afa6bf33ec.r2.cloudflarestorage.com';
const R2_BUCKET = 'tmz';

// CSRF token management for image service
let csrfToken = null;

// Fetch CSRF token from backend
const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

/**
 * Handle API errors
 * @param {Error} error - The error object
 * @returns {Object} - Error response object
 */
const handleApiError = (error) => {
  console.error('Image API Error:', error);
  
  if (error.response) {
    return {
      success: false,
      error: error.response.data.error || 'An error occurred',
      status: error.response.status
    };
  } else if (error.request) {
    return {
      success: false,
      error: 'No response from server',
      status: 503
    };
  } else {
    return {
      success: false,
      error: error.message || 'Unknown error',
      status: 500
    };
  }
};

/**
 * Upload an image to Cloudflare R2 via our API
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - The response from the API
 */
export const uploadImage = async (imageFile) => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    
    const response = await axios.post(`${API_URL}/api/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(csrfToken && { 'x-csrf-token': csrfToken })
      },
      withCredentials: true, // Send HTTP-only cookies automatically
    });
    
    return response.data;
  } catch (error) {
    // If CSRF error, clear token and retry
    if (error.response?.status === 403 && error.response?.data?.error === 'CSRF token validation failed') {
      csrfToken = null;
      await fetchCsrfToken();
      if (csrfToken) {
        return uploadImage(imageFile);
      }
    }
    return handleApiError(error);
  }
};

/**
 * Delete an image from Cloudflare R2
 * @param {string} imageId - The ID/path of the image to delete
 * @returns {Promise<Object>} - The response from the API
 */
export const deleteImage = async (imageId) => {
  try {
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    
    const response = await axios.delete(`${API_URL}/api/images/${imageId}`, {
      headers: {
        ...(csrfToken && { 'x-csrf-token': csrfToken })
      },
      withCredentials: true, // Send HTTP-only cookies automatically
    });
    
    return response.data;
  } catch (error) {
    // If CSRF error, clear token and retry
    if (error.response?.status === 403 && error.response?.data?.error === 'CSRF token validation failed') {
      csrfToken = null;
      await fetchCsrfToken();
      if (csrfToken) {
        return deleteImage(imageId);
      }
    }
    return handleApiError(error);
  }
};

/**
 * Get an image URL
 * @param {string} imageId - The ID/path of the image
 * @returns {string} - The URL for the image
 */
export const getImageUrl = (imageId) => {
  if (!imageId) return '';
  
  // If it's already a full URL, return it
  if (imageId.startsWith('http')) return imageId;
  
  // Otherwise, construct the CDN URL
  return `${CDN_URL}/${imageId}`;
};

/**
 * Check if a URL is a Cloudflare R2 URL
 * @param {string} url - The URL to check
 * @returns {boolean} - Whether the URL is a Cloudflare R2 URL
 */
export const isCloudflareUrl = (url) => {
  return url && (
    url.startsWith(CDN_URL) || 
    url.startsWith(R2_URL) ||
    url.includes(`/${R2_BUCKET}/`)
  );
};

/**
 * Extract the image ID/path from a Cloudflare R2 URL
 * @param {string} url - The URL to extract from
 * @returns {string|null} - The image ID/path or null if not found
 */
export const getImageIdFromUrl = (url) => {
  if (!url) return null;
  
  // Handle cdn.themadzone.com URLs
  if (url.startsWith(CDN_URL)) {
    return url.replace(`${CDN_URL}/`, '');
  }
  
  // Handle R2 URLs
  if (url.startsWith(R2_URL)) {
    return url.replace(`${R2_URL}/`, '');
  }
  
  return null;
};

const imageService = {
  uploadImage,
  deleteImage,
  getImageUrl,
  isCloudflareUrl,
  getImageIdFromUrl
};

export default imageService; 